import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import useAuth from 'src/hooks/useAuth';
import { useDispatch } from 'src/redux/store';
import { createGroup } from 'src/redux/slices/chat';
import actionListaCanaisPorCliente from 'src/actions/clientes/actionListaCanaisPorCliente';
import v2_actionPesquisarContatos from 'src/actions/v2/contatos/v2_actionPesquisarContatos';

import Iconify from 'src/components/Iconify';
import { Button } from 'src/@/components/ui/button';
import { Input } from 'src/@/components/ui/input';
import { Textarea } from 'src/@/components/ui/textarea';
import { Label } from 'src/@/components/ui/label';
import { Separator } from 'src/@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from 'src/@/components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from 'src/@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from 'src/@/components/ui/select';

const phoneClean = (raw) => String(raw || '').replace(/\D/g, '');

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

export default function CreateGroupModal({ open, onClose }) {
  const { t } = useTranslation('chat');
  const { user, cliente } = useAuth();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  // form state
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [canalId, setCanalId] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');

  // canais
  const [canaisWba, setCanaisWba] = useState([]);
  const [loadingCanais, setLoadingCanais] = useState(false);

  // participants
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedParticipants, setSelectedParticipants] = useState([]); // {wa_id, nome}
  const [manualNumber, setManualNumber] = useState('');
  const searchTimer = useRef(null);

  const [submitting, setSubmitting] = useState(false);

  // Fetch canais (filter to whatsapp_business)
  useEffect(() => {
    if (!open || !cliente?._id) return;
    let cancel = false;
    setLoadingCanais(true);
    actionListaCanaisPorCliente(cliente._id)
      .then((res) => {
        if (cancel) return;
        const all = res?.data?.data || [];
        // Endpoint retorna projeção sem `config`; o filtro de fromNumberId acontece
        // no backend ao chamar a Meta API. Aqui só filtramos por platform.
        const wba = all.filter(c => c.platform === 'whatsapp_business' && c.status !== false);
        setCanaisWba(wba);
        if (wba.length === 1) setCanalId(wba[0]._id);
      })
      .catch((err) => {
        if (!cancel) {
          enqueueSnackbar(t('groups.create.errorCanaisLoad', 'Erro ao carregar canais'), { variant: 'error' });
        }
      })
      .finally(() => { if (!cancel) setLoadingCanais(false); });
    return () => { cancel = true; };
  }, [open, cliente?._id, enqueueSnackbar, t]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setSubject('');
      setDescription('');
      setCanalId('');
      setPhotoFile(null);
      setPhotoPreview('');
      setSearch('');
      setSearchResults([]);
      setSelectedParticipants([]);
      setManualNumber('');
    }
  }, [open]);

  // Debounced contact search
  useEffect(() => {
    if (!cliente?._id) return;
    if (!search || search.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      try {
        setSearching(true);
        const res = await v2_actionPesquisarContatos(cliente._id, search.trim());
        const list = res?.data?.data || [];
        setSearchResults(list);
      } catch (err) {
        // silencioso
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => searchTimer.current && clearTimeout(searchTimer.current);
  }, [search, cliente?._id]);

  const isAlreadySelected = useCallback(
    (waId) => selectedParticipants.some(p => p.wa_id === waId),
    [selectedParticipants]
  );

  const addParticipant = useCallback((wa_id, nome) => {
    const cleaned = phoneClean(wa_id);
    if (!cleaned) return;
    if (selectedParticipants.some(p => p.wa_id === cleaned)) return;
    setSelectedParticipants(prev => [...prev, { wa_id: cleaned, nome: nome || '' }]);
  }, [selectedParticipants]);

  const removeParticipant = useCallback((wa_id) => {
    setSelectedParticipants(prev => prev.filter(p => p.wa_id !== wa_id));
  }, []);

  const onAddManual = useCallback(() => {
    const cleaned = phoneClean(manualNumber);
    if (cleaned.length < 10) {
      enqueueSnackbar(t('groups.create.errorManualNumber', 'Número inválido (com DDI)'), { variant: 'warning' });
      return;
    }
    addParticipant(cleaned, '');
    setManualNumber('');
  }, [manualNumber, addParticipant, enqueueSnackbar, t]);

  const onPickPhoto = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      enqueueSnackbar(t('groups.create.errorPhotoType', 'O arquivo deve ser uma imagem'), { variant: 'error' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      enqueueSnackbar(t('groups.create.errorPhotoSize', 'A imagem deve ter no máximo 5MB'), { variant: 'error' });
      return;
    }
    try {
      const base64 = await fileToBase64(file);
      setPhotoFile(file);
      setPhotoPreview(base64);
    } catch (err) {
      enqueueSnackbar(t('groups.create.errorPhotoRead', 'Erro ao ler imagem'), { variant: 'error' });
    }
  }, [enqueueSnackbar, t]);

  const handleSubmit = useCallback(async () => {
    if (!subject.trim()) {
      enqueueSnackbar(t('groups.create.errorSubject', 'Informe o nome do grupo'), { variant: 'error' });
      return;
    }
    if (!canalId) {
      enqueueSnackbar(t('groups.create.errorCanal', 'Selecione o canal'), { variant: 'error' });
      return;
    }
    if (selectedParticipants.length < 1) {
      enqueueSnackbar(t('groups.create.errorParticipants', 'Adicione pelo menos um participante'), { variant: 'error' });
      return;
    }

    try {
      setSubmitting(true);
      const result = await dispatch(createGroup({
        canalId,
        clienteId: cliente?._id || user?.clienteId,
        subject: subject.trim(),
        description: description.trim() || undefined,
        photo: photoPreview || undefined,
        participants: selectedParticipants.map(p => p.wa_id),
        criadoPor: user?.id
      }));
      if (result?.success) {
        enqueueSnackbar(t('groups.create.success', 'Grupo criado com sucesso'), { variant: 'success' });
        onClose?.();
      } else {
        enqueueSnackbar(result?.message || t('groups.create.errorGeneric', 'Erro ao criar grupo'), { variant: 'error' });
      }
    } finally {
      setSubmitting(false);
    }
  }, [subject, description, photoPreview, selectedParticipants, canalId, dispatch, cliente, user, enqueueSnackbar, t, onClose]);

  const filteredResults = useMemo(
    () => searchResults.filter(c => {
      const num = phoneClean(c.numero || c.whatsapp);
      return num && !isAlreadySelected(num);
    }),
    [searchResults, isAlreadySelected]
  );

  return (
    <Dialog open={!!open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="sm:max-w-[640px] p-0 overflow-hidden gap-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-gray-100 dark:border-neutral-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-violet-100 dark:bg-violet-500/20 flex items-center justify-center flex-shrink-0">
              <Iconify icon="solar:users-group-rounded-bold" width={20} className="text-violet-600 dark:text-violet-400" />
            </div>
            <div className="min-w-0">
              <DialogTitle className="text-base font-semibold">{t('groups.create.title', 'Criar grupo')}</DialogTitle>
              <DialogDescription className="text-xs text-gray-500 dark:text-neutral-400">
                {t('groups.create.subtitle', 'WhatsApp Business via Meta Cloud API')}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="max-h-[70vh] overflow-y-auto px-6 py-5 space-y-5">
          {/* Foto + Nome */}
          <div className="flex items-start gap-4">
            <label className="relative cursor-pointer group flex-shrink-0">
              <Avatar className="w-20 h-20 rounded-2xl border-2 border-dashed border-gray-300 dark:border-neutral-700 group-hover:border-violet-400 transition-colors">
                <AvatarImage src={photoPreview} alt="Group photo" />
                <AvatarFallback className="rounded-2xl bg-gray-50 dark:bg-neutral-800 text-gray-400">
                  <Iconify icon="solar:gallery-add-bold" width={22} />
                </AvatarFallback>
              </Avatar>
              <div className="absolute inset-0 rounded-2xl bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                <Iconify icon="solar:camera-add-bold" width={18} className="text-white opacity-0 group-hover:opacity-100" />
              </div>
              {photoPreview && (
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); setPhotoFile(null); setPhotoPreview(''); }}
                  className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center shadow"
                  title={t('groups.create.removePhoto', 'Remover foto')}
                >
                  <Iconify icon="eva:close-outline" width={12} />
                </button>
              )}
              <input type="file" accept="image/*" className="hidden" onChange={onPickPhoto} />
            </label>

            <div className="flex-1 min-w-0 space-y-3">
              <div className="space-y-1.5">
                <Label className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                  {t('groups.create.subject', 'Nome do grupo')}
                </Label>
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder={t('groups.create.subjectPlaceholder', 'Ex: Equipe de vendas')}
                  className="rounded-xl border-gray-200 focus:border-[#7C3AED] focus:ring-[#7C3AED]/20"
                  maxLength={100}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                  {t('groups.create.canal', 'Canal')}
                </Label>
                <Select value={canalId} onValueChange={setCanalId}>
                  <SelectTrigger className="rounded-xl border-gray-200 focus:border-[#7C3AED] focus:ring-[#7C3AED]/20 h-9">
                    <SelectValue placeholder={
                      loadingCanais
                        ? t('groups.create.canaisLoading', 'Carregando canais...')
                        : (canaisWba.length === 0
                            ? t('groups.create.canaisEmpty', 'Nenhum canal WhatsApp Business disponível')
                            : t('groups.create.canalPlaceholder', 'Selecione o canal'))
                    } />
                  </SelectTrigger>
                  <SelectContent>
                    {canaisWba.map(c => (
                      <SelectItem key={c._id} value={c._id}>
                        <div className="flex items-center gap-2">
                          <Iconify icon="logos:whatsapp-icon" width={14} />
                          <span className="font-medium">{c.nome || c._id}</span>
                          {c.numero_whatsapp && (
                            <span className="text-[10px] text-gray-500">+{c.numero_whatsapp}</span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
              {t('groups.create.description', 'Descrição (opcional)')}
            </Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('groups.create.descriptionPlaceholder', 'Sobre o que é este grupo...')}
              rows={2}
              className="rounded-xl border-gray-200 focus:border-[#7C3AED] focus:ring-[#7C3AED]/20"
              maxLength={512}
            />
          </div>

          <Separator className="bg-gray-200/60" />

          {/* Participantes */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                {t('groups.create.participants', 'Participantes')}
              </Label>
              <span className="text-[11px] text-gray-500">
                {t('groups.create.participantsCount', '{{count}} participantes', { count: selectedParticipants.length })}
              </span>
            </div>

            {/* Selected chips */}
            {selectedParticipants.length > 0 && (
              <div className="flex flex-wrap gap-1.5 p-2 rounded-xl bg-violet-50/50 dark:bg-violet-500/5 border border-violet-100 dark:border-violet-500/20">
                {selectedParticipants.map(p => (
                  <span
                    key={p.wa_id}
                    className="inline-flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-full bg-white dark:bg-neutral-800 border border-violet-200 dark:border-violet-500/30 text-[11px] shadow-sm"
                  >
                    <Iconify icon="solar:user-rounded-bold" width={12} className="text-violet-500" />
                    <span className="font-medium text-gray-800 dark:text-neutral-200">
                      {p.nome || `+${p.wa_id}`}
                    </span>
                    {p.nome && <span className="text-gray-400">+{p.wa_id}</span>}
                    <button
                      type="button"
                      onClick={() => removeParticipant(p.wa_id)}
                      className="w-4 h-4 rounded-full bg-gray-200 hover:bg-red-100 dark:bg-neutral-700 dark:hover:bg-red-500/20 text-gray-500 hover:text-red-600 flex items-center justify-center transition-colors"
                    >
                      <Iconify icon="eva:close-outline" width={10} />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Search bar */}
            <div className="relative">
              <Iconify icon="eva:search-outline" width={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('groups.create.searchPlaceholder', 'Buscar contato pelo nome ou número...')}
                className="pl-9 rounded-xl border-gray-200 focus:border-[#7C3AED] focus:ring-[#7C3AED]/20"
              />
              {searching && (
                <Iconify icon="svg-spinners:ring-resize" width={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-violet-500" />
              )}
            </div>

            {/* Search results */}
            {search.trim().length >= 2 && (
              <div className="rounded-xl border border-gray-200 dark:border-neutral-800 max-h-44 overflow-y-auto">
                {filteredResults.length === 0 && !searching && (
                  <div className="px-4 py-6 text-center text-xs text-gray-400">
                    {t('groups.create.searchEmpty', 'Nenhum contato encontrado')}
                  </div>
                )}
                {filteredResults.map(c => {
                  const num = phoneClean(c.numero || c.whatsapp);
                  return (
                    <button
                      type="button"
                      key={c._id || num}
                      onClick={() => { addParticipant(num, c.nome); setSearch(''); setSearchResults([]); }}
                      className="w-full flex items-center gap-3 px-3 py-2 hover:bg-violet-50 dark:hover:bg-violet-500/10 transition-colors text-left"
                    >
                      <Avatar className="w-7 h-7">
                        <AvatarImage src={c.fotoPerfil} />
                        <AvatarFallback className="text-[10px] bg-gray-200 dark:bg-neutral-700">
                          {(c.nome || '?').charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">{c.nome || `+${num}`}</p>
                        <p className="text-[11px] text-gray-500 truncate">+{num}</p>
                      </div>
                      <Iconify icon="eva:plus-outline" width={14} className="text-violet-500 flex-shrink-0" />
                    </button>
                  );
                })}
              </div>
            )}

            {/* Manual number entry */}
            <div className="flex items-center gap-2 pt-1">
              <Input
                value={manualNumber}
                onChange={(e) => setManualNumber(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onAddManual(); } }}
                placeholder={t('groups.create.manualPlaceholder', 'Adicionar por número (com DDI). Ex: 5511999998888')}
                className="flex-1 rounded-xl border-gray-200 focus:border-[#7C3AED] focus:ring-[#7C3AED]/20 font-mono text-xs"
              />
              <Button
                type="button"
                variant="outline"
                onClick={onAddManual}
                className="rounded-xl"
              >
                <Iconify icon="eva:plus-outline" width={14} className="mr-1" />
                {t('groups.create.addManual', 'Adicionar')}
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t border-gray-100 dark:border-neutral-800 bg-gray-50/80 dark:bg-neutral-900/80 sm:justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            {t('groups.create.cancel', 'Cancelar')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-[#7C3AED] hover:bg-[#6d28d9] text-white shadow-sm shadow-[#7C3AED]/20"
          >
            {submitting ? (
              <>
                <Iconify icon="svg-spinners:ring-resize" width={14} className="mr-2" />
                {t('groups.create.submitting', 'Criando...')}
              </>
            ) : (
              <>
                <Iconify icon="solar:add-circle-bold" width={14} className="mr-2" />
                {t('groups.create.submit', 'Criar grupo')}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
